import { matchPassioRoute, type PassioCallRecord } from "@passio/api-client";
import { pyLiteral } from "./chainCode";
import { publicApiBase, publicApiOrigin } from "./clients";

const SKIP = new Set(["accept", "content-type", "authorization", "passio-id"]);

export function pathAndQuery(r: PassioCallRecord) {
  const i = r.url.indexOf("/napi/");
  return i >= 0 ? r.url.slice(i) : r.path;
}

function inputHeaders(r: PassioCallRecord): [string, string][] {
  return Object.entries(r.requestHeaders).filter(([k]) => !SKIP.has(k.toLowerCase()));
}

const shq = (s: string) => `'${s.replace(/'/g, `'\\''`)}'`;

function truncateDataUris(v: unknown): unknown {
  if (typeof v === "string") return v.startsWith("data:") && v.length > 120 ? `${v.slice(0, 60)}…(${v.length} chars)` : v;
  if (Array.isArray(v)) return v.map(truncateDataUris);
  if (v && typeof v === "object") return Object.fromEntries(Object.entries(v).map(([k, x]) => [k, truncateDataUris(x)]));
  return v;
}

const tokenUrl = (key: string) => `${publicApiOrigin()}/v2/token-cache/unified/oauth/token/${key}`;

export function toCurl(r: PassioCallRecord): string {
  const auth = [
    `# PASSIO_TOKEN and PASSIO_CUSTOMER_ID are access_token and customer_id from (cache until expires_in):`,
    `#   curl "${tokenUrl("$PASSIO_KEY")}"`,
  ].join("\n");
  const lines = [`curl -X ${r.method} ${shq(publicApiBase() + pathAndQuery(r))}`, `  -H "Authorization: Bearer $PASSIO_TOKEN"`, `  -H "Passio-ID: $PASSIO_CUSTOMER_ID"`];
  for (const [k, v] of inputHeaders(r)) lines.push(`  -H ${shq(`${k}: ${v}`)}`);
  if (r.requestBody !== undefined) {
    lines.push(`  -H 'Content-Type: application/json'`);
    lines.push(`  -d ${shq(JSON.stringify(truncateDataUris(r.requestBody)))}`);
  }
  return `${auth}\n${lines.join(" \\\n")}`;
}

export function toPython(r: PassioCallRecord): string {
  const extra = inputHeaders(r).map(([k, v]) => `    ${JSON.stringify(k)}: ${JSON.stringify(v)},`);
  const body = r.requestBody !== undefined ? `\n    json=${pyLiteral(truncateDataUris(r.requestBody), "    ")},` : "";
  return `import os

import requests

HOST = "${publicApiOrigin()}"

# Server-side only. Exchange your license key for a token and cache it until expires_in runs out.
auth = requests.get(f"{HOST}/v2/token-cache/unified/oauth/token/{os.environ['PASSIO_KEY']}", timeout=30).json()
headers = {
    "Authorization": f"Bearer {auth['access_token']}",
    "Passio-ID": auth["customer_id"],
${extra.join("\n")}${extra.length ? "\n" : ""}}

res = requests.${r.method.toLowerCase()}(
    f"{HOST}/v2/products${pathAndQuery(r).replace(/[{}]/g, (c) => c + c)}",
    headers=headers,${body}
    timeout=120,
)
res.raise_for_status()
data = res.json()
print(res.headers.get("X-Request-ID"), res.headers.get("X-Request-Usage"), data)`;
}

export function toTypeScript(r: PassioCallRecord): string {
  const route = matchPassioRoute(r.method, r.path);
  const url = new URL(r.url, "http://x");
  const query = Object.fromEntries(url.searchParams.entries());
  const h = Object.fromEntries(inputHeaders(r));
  const headerInit: string[] = [];
  if (h["Localization-ISO"]) headerInit.push(`  localizationIso: ${JSON.stringify(h["Localization-ISO"])},`);
  if (h["Passio-Product-Localization"]) headerInit.push(`  productLocalization: ${JSON.stringify(h["Passio-Product-Localization"])},`);
  if (h["Passio-Search-Config"]) headerInit.push(`  searchConfig: ${h["Passio-Search-Config"]},`);
  if (h["X-Passio-Product-Images"]) headerInit.push(`  productImages: true,`);
  const opts = [Object.keys(query).length ? `query: ${JSON.stringify(query)}` : "", r.requestBody !== undefined ? `body: ${JSON.stringify(truncateDataUris(r.requestBody), null, 2)}` : ""]
    .filter(Boolean)
    .join(", ");
  return `import { PassioClient, LicenseKeyAuth, PassioRequestHeaders } from "@passio/api-client";

const passio = new PassioClient({
  baseUrl: "${publicApiBase()}",
  auth: new LicenseKeyAuth({ licenseKey: process.env.PASSIO_KEY!, host: "${publicApiOrigin()}" }), // server-side only
  headers: new PassioRequestHeaders({
${headerInit.join("\n") || "  // localizationIso, productLocalization, searchConfig, productImages"}
  }),
});

// ${route ? `${route.title}: typed helper available as one of passio.foods / tools / vision / affiliates / feedback (${route.id})` : "custom route"}
const { data, meta } = await passio.request("${r.method}", "${r.path}"${opts ? `, { ${opts} }` : ""});
console.log(meta.requestId, meta.requestUsage, data);`;
}

export function toFetch(r: PassioCallRecord): string {
  const headers: Record<string, string> = { Authorization: "Bearer ${token}", "Passio-ID": "${customerId}", ...Object.fromEntries(inputHeaders(r)) };
  if (r.requestBody !== undefined) headers["Content-Type"] = "application/json";
  return `const res = await fetch("${publicApiBase()}${pathAndQuery(r)}", {
  method: "${r.method}",
  headers: ${JSON.stringify(headers, null, 2).replace(/"\$\{(\w+)\}"/g, "`${$1}`").replace(/\n/g, "\n  ")},${
    r.requestBody !== undefined ? `\n  body: JSON.stringify(${JSON.stringify(truncateDataUris(r.requestBody), null, 2).replace(/\n/g, "\n  ")}),` : ""
  }
});
const data = await res.json();
const tokensUsed = res.headers.get("X-Request-Usage");`;
}
